import { connect, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { Form, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';

import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';

import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { IDictionaryKnoOrganizationSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import styles from './AccessSettingsModal.module.scss';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import {
  knmAddVisibleModal,
  knmSetAccessSettingsFormRef,
  knmUpdateAccessPrivilege,
} from '../../../KnmPackage/MainModule/actions/knmActions';
import { KnmModalsEnum } from '../../../KnmPackage/MainModule/enums/KnmModalsEnum';
import { IKnmFormAccessPrivilege } from '../../../KnmPackage/MainModule/interfaces/IKnmFormAccessPrivilege';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { IKnmAccessLevelSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmAccessLevelSelectOption';
import { getKnmAccessLevelSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmAccessPrivilege } from '../../../APIPackage/KnmModule/interfaces/IKnmAccessPrivilege';
import { catalogGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { Notification } from '../../../CorePackage/NotificationModule/components/Notification';
import { INotification } from '../../../CorePackage/NotificationModule/interfaces/INotification';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { getKnmInitialAccessSettingsFormValues } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { getUserRoleIsProsecutorOrSuperProsecutor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { IKnmErknmFormCollaboratingOrganization } from '../../../KnmPackage/MainModule/interfaces/IKnmErknmFormCollaboratingOrganization';
import { LawInterfacesEnum } from '../../../CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from '../../../CorePackage/MainModule/reducers/coreReducer';

export interface IAccessSettingsForm {
  accessPrivilege: IKnmFormAccessPrivilege[] | undefined;
}

interface IAccessSettingsModalProps {
  visible: boolean;
  onClose: () => void;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  collaboratingOrganizations: IKnmErknmFormCollaboratingOrganization[] | undefined;
}

export type IAccessSettingsModalRenderProps = IAccessSettingsModalProps & FormRenderProps<IAccessSettingsForm>;

export interface IAccessSettingsFormRef {
  readonly current: IAccessSettingsModalRenderProps['form'];
}

interface IAccessSettingsModalStoreProps {
  accessLevelSelectOptions: IKnmAccessLevelSelectOption[];
  initialFormValues: IAccessSettingsForm | undefined;
  isProsecutorOrSuperProsecutor: boolean;
  lawInterface: LawInterfacesEnum;
}

const SAVE_ERROR_NOTIFICATION: INotification = {
  noticeType: NoticeTypesEnum.ERROR,
  messageType: MessageTypesEnum.SAVE_KNM,
  renderContent: 'Изменения не сохранены. Требуется исправить ошибки ',
};

const AccessSettingsModalRender = ({
  visible,
  onClose,
  form,
  knoOrganization,
  accessLevelSelectOptions,
  handleSubmit,
  hasValidationErrors,
  initialFormValues,
  dirty,
  isProsecutorOrSuperProsecutor,
  collaboratingOrganizations,
  lawInterface,
}: IAccessSettingsModalRenderProps & IAccessSettingsModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const setFormRef = useCallback((ref: IAccessSettingsFormRef) => dispatch(knmSetAccessSettingsFormRef(ref)), [
    dispatch,
  ]);
  const accessSettingsFormRef = useRef(form);
  const [isShowNotification, setIsShowNotification] = useState(false);

  accessSettingsFormRef.current = form;

  useEffect(() => {
    setFormRef(accessSettingsFormRef);
  }, [setFormRef, accessSettingsFormRef]);

  useEffect(() => {
    accessSettingsFormRef.current.reset(initialFormValues);
  }, [accessSettingsFormRef, initialFormValues]);

  const { remove } = accessSettingsFormRef.current.mutators;
  const ownKnoTitle = knoOrganization?.title || '';
  const accessPrivilegeFieldName = 'accessPrivilege' as keyof IAccessSettingsForm;

  const isErknm = lawInterface === LawInterfacesEnum.FZ_248;

  useEffect(() => {
    if (isErknm) {
      dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_ACCESS_LEVELS));
    }
  }, [dispatch]);

  useEffect(() => {
    setIsShowNotification(false);
  }, [visible, setIsShowNotification]);

  const handleAddField = useCallback(() => {
    dispatch(knmAddVisibleModal(KnmModalsEnum.DICTIONARY_KNO_ORGANISATIONS));
  }, []);

  const handleRemoveField = useCallback(
    (index: number) => {
      remove && remove(accessPrivilegeFieldName, index);
    },
    [remove],
  );

  const handleFormSubmit = useCallback(() => {
    handleSubmit();
    hasValidationErrors && setIsShowNotification(true);
  }, [handleSubmit, hasValidationErrors, setIsShowNotification]);

  const handleCloseNotification = useCallback(() => {
    setIsShowNotification(false);
  }, [setIsShowNotification]);

  const getIsItemWatchOnly = useCallback(
    (dictVersionId: string | undefined): boolean => {
      if (!dictVersionId) return false;

      return (
        knoOrganization?.value.dictVersionId === dictVersionId ||
        collaboratingOrganizations
          ?.map(item => item.organization)
          .some(x => x?.guid === dictVersionId || x?.value.dictVersionId === dictVersionId) ||
        false
      );
    },
    [knoOrganization, collaboratingOrganizations],
  );

  return (
    <Modal visible={visible} onClose={onClose}>
      <ModalHeader title="Настройка прав доступа" />
      {isShowNotification && (
        <div className={styles.Notification}>
          <Notification notice={SAVE_ERROR_NOTIFICATION} close={handleCloseNotification} />
        </div>
      )}
      <ModalBody size="content">
        <div>
          <div className={styles.InfoBlock}>
            <span className={styles.InfoIcon}></span>
            <span className={styles.InfoText}>
              Владельцем мероприятия является {ownKnoTitle}, изменение условий доступа владельца мероприятия не
              осуществляется
            </span>
          </div>
          <div className={styles.AddBlock}>
            Условия доступа к мероприятию
            {!isProsecutorOrSuperProsecutor && (
              <Button
                text="Добавить"
                variant="link"
                type="button"
                color="primary"
                className="shared-btn-add"
                onClick={handleAddField}
              />
            )}
          </div>
        </div>
        <div className={styles.Table}>
          <div className={styles.TableHeader}>
            <div>№</div>
            <div>КОНТРОЛЬНЫЙ (НАДЗОРНЫЙ) ОРГАН</div>
            <div>УРОВЕНЬ ДОСТУПА</div>
            <div></div>
          </div>
          <div className={styles.TableContent}>
            <FieldArray name={accessPrivilegeFieldName}>
              {({ fields }) =>
                fields.map((name, index) => {
                  const fieldValue: IKnmFormAccessPrivilege = fields.value[index];
                  const fullName = fieldValue.organisation?.fullName;
                  const selectInputFieldName = `${name}.accessLevel` as keyof IAccessSettingsForm;
                  const isItemWatchOnly = getIsItemWatchOnly(fieldValue.organisation?.dictVersionId);

                  return (
                    <div key={name} className={styles.TableRow}>
                      <div>{index + 1}</div>
                      <div>{fullName}</div>
                      <div>
                        <SelectInputField
                          name={selectInputFieldName}
                          options={accessLevelSelectOptions}
                          validate={makeRequired('Поле не может быть пустым')}
                          disabled={isProsecutorOrSuperProsecutor || isItemWatchOnly}
                        />
                      </div>
                      <div>
                        {!isProsecutorOrSuperProsecutor && !isItemWatchOnly && (
                          <CloseButton className={styles.CloseButton} onClick={() => handleRemoveField(index)} />
                        )}
                      </div>
                    </div>
                  );
                })
              }
            </FieldArray>
          </div>
        </div>
      </ModalBody>
      <ModalActions>
        <Button text="Закрыть" size="medium" type="button" onClick={onClose} />
        <Button
          text="Сохранить"
          color="primary"
          id="saveButton"
          size="medium"
          type="button"
          onClick={handleFormSubmit}
          disabled={!dirty}
        />
      </ModalActions>
    </Modal>
  );
};

const AccessSettingsModalWithoutStore = ({ ...rest }: IAccessSettingsModalProps & IAccessSettingsModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const renderForm = (props: FormRenderProps<IAccessSettingsForm>) => {
    return <AccessSettingsModalRender {...props} {...rest} />;
  };

  const handleSubmit = useCallback((values: IAccessSettingsForm) => {
    const accessPrivileges: IKnmAccessPrivilege[] =
      values.accessPrivilege?.map(item => ({
        dictVersionId: item.organisation?.dictVersionId || item.organisation?.ervkVersionUuid || '',
        accessPrivilegeLevel: item.accessLevel
          ? { id: item.accessLevel.id, name: item.accessLevel.name, code: item.accessLevel.code }
          : null,
        guid: item.guid,
      })) || [];

    dispatch(knmUpdateAccessPrivilege(accessPrivileges));
  }, []);

  return <Form<IAccessSettingsForm> onSubmit={handleSubmit} render={renderForm} mutators={{ ...arrayMutators }} />;
};

const mapStateToProps = (state: IRootState): IAccessSettingsModalStoreProps => ({
  accessLevelSelectOptions: getKnmAccessLevelSelectOptions(state),
  initialFormValues: getKnmInitialAccessSettingsFormValues(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  lawInterface: getLawInterface(state),
});

export const AccessSettingsModal = connect(mapStateToProps)(AccessSettingsModalWithoutStore);
